import * as _ from 'lodash';
import { TromanError } from '../../common/TromanError';
import { AuditData } from './Audit';

/**
 * @typedef { import('../server/ResponseContext').ResponseContext } ResponseContext
 * @typedef { import('../objects/Meeting').Meeting } Meeting
 */

export const timeReportState = Object.freeze({
    none: 0,
    requested: 1,
    reported: 2,
    rejected: 3,
    accepted: 4,
    recalled: 5,
});

export const timeReportCommand = Object.freeze({
    none: 0,
    add: 1,
    accept: 2,
    reject: 3,
});

export class Attendance {
    constructor(obj) {
        if (_.isObjectLike(obj)) {
            _.assign(this, obj);

            if (this.TimeReport) {
                this.TimeReport.AuditEntries = _.map(this.TimeReport.AuditEntries, x => new AuditData(x));
            }
        }
    }

    /**
     * @param {ResponseContext} ctx
     */
    resolve(ctx) {
        this.Meeting = ctx.getMeeting(this.MeetingId);

        if (this.MissionId != null) {
            this.Mission = ctx.getMission(this.MissionId);
        } else {
            this.Person = ctx.getPerson(this.PersonId);
            this.Organization = ctx.getOrganization(this.OrganizationId);
            this.MissionType = ctx.getMissionType(this.MissionTypeId);
        }

        if (this.TimeReport) {
            _.forEach(this.TimeReport.AuditEntries, x => x.resolve(ctx));
        }

        this._updateDisplay();
    }

    _updateDisplay() {
        if (this.MissionId != null) {
            this.displayPerson = this.Mission.Person.name;
            if (this.Mission.Party != null) {
                this.displayPerson = this.displayPerson + ' (' + this.Mission.Party.Abbreviation + ')';
            }
            if (this.Mission.MandatePeriod.OrganizationId === this.Meeting.OrganizationId) {
                this.displayMissionType = this.Mission.MissionType.Name;
            } else {
                this.displayMissionType = `(${this.Mission.MissionType.Abbreviation}) ${this.Mission.MandatePeriod.Organization.Name}`;
            }
        } else {
            this.displayPerson = this.Person.display;
            if (this.MissionType != null) {
                if (this.Organization != null) {
                    this.displayMissionType = `(${this.MissionType.Abbreviation}) ${this.Organization.Name}`;
                } else {
                    this.displayMissionType = this.MissionType.Name;
                }
            } else {
                if (this.Organization != null) {
                    this.displayMissionType = '(?) ' + this.Organization.Name;
                } else {
                    this.displayMissionType = '';
                }
            }
        }
    }

    isLegacy() {
        return this.MissionId == null;
    }

    getPersonId() {
        if (this.MissionId != null) {
            return this.Mission.PersonId;
        } else {
            return this.PersonId;
        }
    }

    getPerson() {
        if (this.MissionId != null) {
            return this.Mission.Person;
        } else {
            return this.Person;
        }
    }

    getOrganization() {
        if (this.MissionId != null) {
            return this.Mission.MandatePeriod.Organization;
        } else {
            return this.Organization;
        }
    }

    getMissionType() {
        if (this.MissionId != null) {
            return this.Mission.MissionType;
        } else {
            return this.MissionType;
        }
    }

    canServeAsSubstitute() {
        let mt = this.getMissionType();
        return mt != null && mt.IsSubstitute;
    }

    /**
     * Creates a new attendance that is a copy of this, but uses the specified
     * mission instead of legacy person/organization/missiontype.
     * @param {any} mission
     */
    specifyMission(mission) {
        if (!this.isLegacy()) throw new TromanError('specifymission_nolegacy');
        if (mission.PersonId != this.PersonId) throw new TromanError('specifymission_wrongperson');
        
        var src = _.clone(this);
        src.Meeting = null;
        src.Mission = null;
        src.Person = null;
        src.PersonId = null;
        src.Organization = null;
        src.OrganizationId = null;
        src.MissionType = null;
        src.MissionTypeId = null;
        
        var result = _.cloneDeep(src);
        result.Meeting = this.Meeting;
        result.Mission = mission;
        result.MissionId = mission.Id;
        result._updateDisplay();

        return result;
    }

    static createNew(meeting, mission) {
        const att = new Attendance();

        att.Meeting = meeting;
        att.MeetingId = meeting.Id;
        att.Mission = mission;
        att.MissionId = mission.Id;

        att.PresentTime = null;
        att.ServingTime = null;

        att._updateDisplay();

        return att;
    }
}


export class AttendanceList {

    /**
     * @param {Meeting} meeting
     * @param {Attendance[]} attendances
     */
    constructor(meeting, attendances) {
        this.Meeting = meeting;
        this.Attendances = attendances;
    }

    static fromResponse(rsp) {
        let result = rsp.getResult();
        let context = rsp.context;
        let meeting = context.getMeeting(result.MeetingId);
        let attendances = result.Attendances.map(x => context.getAttendance(x));
        return new AttendanceList(meeting, attendances);
    }
}

function getOrder(att) {
    if (att.Mission && att.Mission.MandatePeriod.OrganizationId === att.Meeting.OrganizationId) {
        return att.Mission.OrderNumber;
    } else {
        return Number.MAX_SAFE_INTEGER;
    }
}

function getOrganization(att) {
    const org = att.getOrganization();
    return org ? org.Name : undefined;
}

export function getAttendanceOrdering(getAttendance) {
    if (getAttendance) {
        return [x => getOrder(getAttendance(x)), x => getOrganization(getAttendance(x))];
    } else {
        return [getOrder, getOrganization];
    }
}
